<template>
  <div class="page">
    <PureBiddingToolbar
      :totalPages="totalPages"
      :currentPage="currentPage"
      :filterOpen="showFilters"
      :highlightMode="highlightMode"
      :highlightPalette="highlightPalette"
      :orders="orders"
      :columnOrder="tableState.columnOrder"
      :columnVisibility="tableState.columnVisibility"
      :scale="scale"
      :totalItems="totalItems"
      @toggleFilter="toggleFilters"
      @columnOrderChanged="onColumnOrder"
      @columnVisibilityChanged="onColumnVisibility"
      @resetSettings="onResetSettings"
      @scaleChanged="scale = $event"
    />

    <div v-if="hasError" class="error-block">
      <p>Ошибка загрузки данных. Попробуйте позже.</p>
    </div>

    <div v-else class="page-layout" :class="{ 'has-filters': showFilters }">
      <div class="table-area">
        <PureBiddingTable
          :orders="orders"
          :isLoading="loading"
          :isRefreshing="isFetching"
          :highlightMode="highlightMode"
          :palette="highlightPalette"
          :columnOrder="tableState.columnOrder"
          :columnVisibility="tableState.columnVisibility"
          :columnSizing="tableState.columnSizing"
          :scale="scale"
          @columnSizingChanged="onColumnSizing"
        />
      </div>

      <aside v-if="showFilters" class="filters-sidebar">
        <PureBiddingFilters />
      </aside>
    </div>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import PureBiddingTable from './components/PureBiddingTable.vue';
import PureBiddingFilters from './components/PureBiddingFilters.vue';
import PureBiddingToolbar from './components/PureBiddingToolbar.vue';
import { useUserTableScale } from '@/components/UI/useUserTableScale';
import { getBrowserStorage } from '@/components/UI/tableScale.mjs';
import {
  defaultTableState,
  loadTableState,
  saveTableState,
} from './pureBiddingTableState.mjs';

export default {
  name: 'PureBiddingPage',
  components: { PureBiddingTable, PureBiddingFilters, PureBiddingToolbar },
  setup() {
    const store = useStore();
    const showFilters = ref(false);
    const hasError = ref(false);
    const isFetching = ref(false);
    let pollTimer = null;

    const storage = getBrowserStorage();
    const tableState = reactive(loadTableState(storage));
    const { scale, resetScale } = useUserTableScale('pure-bidding');
    watch(tableState, (value) => saveTableState(storage, value), { deep: true });

    const onColumnOrder = (order) => { tableState.columnOrder = order; };
    const onColumnVisibility = (visibility) => { tableState.columnVisibility = visibility; };
    const onColumnSizing = (sizing) => { tableState.columnSizing = sizing; };
    const onResetSettings = () => {
      Object.assign(tableState, defaultTableState());
      resetScale();
    };

    const state = computed(() => store.state.pureBidding);

    const toggleFilters = () => {
      showFilters.value = !showFilters.value;
    };

    const fetchData = () => {
      if (isFetching.value) return;
      isFetching.value = true;
      store.dispatch('pureBidding/getOrders')
        .then(() => { hasError.value = false; })
        .catch(() => { hasError.value = true; })
        .finally(() => { isFetching.value = false; });
    };

    onMounted(() => {
      fetchData();
      pollTimer = setInterval(fetchData, 30000);
    });

    onBeforeUnmount(() => {
      if (pollTimer) clearInterval(pollTimer);
    });

    return {
      showFilters,
      toggleFilters,
      hasError,
      isFetching,
      tableState,
      scale,
      onColumnOrder,
      onColumnVisibility,
      onColumnSizing,
      onResetSettings,
      orders: computed(() => (Array.isArray(state.value?.orders) ? state.value.orders : [])),
      loading: computed(() => Boolean(state.value?.loading)),
      currentPage: computed(() => state.value?.page || 1),
      totalPages: computed(() => state.value?.totalPages || 1),
      totalItems: computed(() => state.value?.totalItems || 0),
      highlightMode: computed(() => state.value?.highlightMode || 'none'),
      highlightPalette: computed(() => state.value?.highlightPalette || 'colorful'),
    };
  },
};
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.page-layout {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.page-layout.has-filters .table-area {
  flex: 1;
}

.table-area {
  flex: 1;
  overflow: auto;
  padding: 0 12px 12px;
}

.filters-sidebar {
  width: 280px;
  flex-shrink: 0;
  border-left: 1px solid #e5e7eb;
  overflow-y: auto;
  background: #fafafa;
}

.error-block {
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 14px;
}
</style>
